import React, { JSX } from 'react'
import { AnalyticsMetafield, ReferringSites } from 'types/api'
import { cleanVariables, graphqlRequest } from 'lib/helpers'
import { RefreshingBlock } from './Loading'
import getReferringSites from '../graphql/getReferringSites.graphql'
import { useQuery } from '@tanstack/react-query'
import { Leaderboard } from './Leaderboard'

export interface ReferringSitesLeaderboardProps {
  filters: {
    productOption?: AnalyticsMetafield[]
    bundleOption?: AnalyticsMetafield[]
    range: {
      startDate: Date
      endDate: Date
    }
    project?: AnalyticsMetafield
  }
  token?: string
  client: AnalyticsMetafield | null
}

export const ReferringSitesLeaderboard = ({ token, client, filters }: ReferringSitesLeaderboardProps): JSX.Element => {
  const {
    data,
    isFetching
  } = useQuery({
    queryKey: ['dashboard', 'referringSites', filters, client?.id],

    queryFn: async () => {
      if (token == null) return

      const cleanVar = cleanVariables({
        clientId: client?.id,
        projectId: filters.project?.id,
        productIds: filters?.productOption?.map(p => p.id),
        startDate: filters.range.startDate,
        endDate: filters.range.endDate
      })
      const { referringSites } = await graphqlRequest<{ referringSites: ReferringSites[] }>(
        getReferringSites, cleanVar, token
      )

      return referringSites
    },

    enabled: token != null && client != null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  return (
    <div data-testid='referring-site-leaderboard' className='relative shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden p-4 min-h-[280px] flex flex-col'>
      {isFetching && RefreshingBlock}
      <Leaderboard
        noDataMessage='No referring sites data for specified filters'
        hoverData={false}
        idField='referringSite'
        data={data ?? []}
        defaultSortKey='sales'
        columns={['referringSite', 'sales']}
        title='Top referring sites'
        limit={10}
      />
    </div>
  )
}
